// ─────────────────────────────────────────────────────────────────────────────
// DynamicFormBuilder.tsx  — formulaires dynamiques + module palette BPMN
// ─────────────────────────────────────────────────────────────────────────────
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "boolean"
  | "select"
  | "multiselect"
  | "radio"
  | "file"
  | "user"
  | "separator";

export interface SelectOption {
  label: string;
  value: string;
}

export interface FormField {
  id: string;
  type: FieldType;
  code: string;
  label: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
  readOnly?: boolean;
  hidden?: boolean;
  helpText?: string;
  options?: SelectOption[];
  min?: number;
  max?: number;
  pattern?: string;
  conditionField?: string;
  conditionValue?: string;
}

export interface DynamicFormSchema {
  id: string;
  label: string;
  fields: FormField[];
  updatedAt: string;
}

export type FormsIndex = Record<string, DynamicFormSchema>;

export interface FormManager {
  forms: FormsIndex;
  isOpen: boolean;
  editingFormId: string | null;
  openManager: (editId?: string) => void;
  closeManager: () => void;
  createForm: () => void;
  deleteForm: (id: string) => void;
  saveForm: (schema: DynamicFormSchema) => void;
  setEditingId: (id: string | null) => void;
  getFormById: (id: string) => DynamicFormSchema | null;
  formList: DynamicFormSchema[];
  flushToModeler: () => void;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const FIELD_TYPES: { type: FieldType; label: string; icon: string }[] = [
  { type: "text", label: "Texte court", icon: "ti-forms" },
  { type: "textarea", label: "Texte long", icon: "ti-text-size" },
  { type: "number", label: "Nombre", icon: "ti-hash" },
  { type: "date", label: "Date", icon: "ti-calendar" },
  { type: "boolean", label: "Booléen", icon: "ti-toggle-right" },
  { type: "select", label: "Liste déroulante", icon: "ti-list" },
  { type: "multiselect", label: "Cases à cocher", icon: "ti-checks" },
  { type: "radio", label: "Boutons radio", icon: "ti-circle-dot" },
  { type: "file", label: "Fichier", icon: "ti-file-upload" },
  { type: "user", label: "Utilisateur", icon: "ti-user" },
  { type: "separator", label: "Séparateur", icon: "ti-separator" },
];

const NODE_SELECTOR_ID = "dfb-node-selector-root";
const PANEL_SELECTOR = "#js-properties-panel";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid(): string {
  return Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 5);
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function uniqueTypes(fields: FormField[] | undefined | null): FieldType[] {
  if (!fields || !fields.length) return [];
  const seen: Record<string, boolean> = {};
  const result: FieldType[] = [];
  fields.forEach((f) => {
    if (!seen[f.type]) {
      seen[f.type] = true;
      result.push(f.type);
    }
  });
  return result;
}

function readProcessForms(modeler: any): FormsIndex {
  try {
    const canvas = modeler.get("canvas") as any;
    const root = canvas.getRootElement();
    const raw = root?.businessObject?.forms;
    if (!raw) return {};
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function writeProcessForms(modeler: any, forms: FormsIndex) {
  try {
    const canvas = modeler.get("canvas") as any;
    const modeling = modeler.get("modeling") as any;
    const root = canvas.getRootElement();
    modeling.updateProperties(root, { forms: JSON.stringify(forms) });
  } catch (e) {
    console.warn("[DFB] writeProcessForms:", e);
  }
}

// ─── useFormManager ───────────────────────────────────────────────────────────

export function useFormManager(
  modelerRef: React.MutableRefObject<any>,
): FormManager {
  const [forms, setForms] = useState<FormsIndex>({});
  const [isOpen, setIsOpen] = useState(false);
  const [editingFormId, setEditingFormId] = useState<string | null>(null);

  const syncFromModeler = useCallback(() => {
    const m = modelerRef.current;
    if (!m) return;
    const loaded = readProcessForms(m);
    if (Object.keys(loaded).length > 0) setForms(loaded);
  }, [modelerRef]);

  useEffect(() => {
    if (isOpen) syncFromModeler();
  }, [isOpen, syncFromModeler]);

  // formsRef : toujours à jour, utilisé par flushToModeler
  const formsRef = useRef<FormsIndex>({});

  const persist = useCallback(
    (next: FormsIndex) => {
      formsRef.current = next;
      setForms(next);
      if (modelerRef.current) writeProcessForms(modelerRef.current, next);
    },
    [modelerRef],
  );

  // Appelé par AttachJsonPage avant saveBPMNToAdmin pour forcer la sync
  const flushToModeler = useCallback(() => {
    if (modelerRef.current && Object.keys(formsRef.current).length > 0) {
      writeProcessForms(modelerRef.current, formsRef.current);
    }
  }, [modelerRef]);

  const openManager = useCallback(
    (editId?: string) => {
      syncFromModeler();
      setEditingFormId(editId ?? null);
      setIsOpen(true);
    },
    [syncFromModeler],
  );

  const closeManager = useCallback(() => {
    setIsOpen(false);
    setEditingFormId(null);
  }, []);

  const createForm = useCallback(() => {
    const schema: DynamicFormSchema = {
      id: uid(),
      label: "Nouveau formulaire",
      fields: [],
      updatedAt: new Date().toISOString(),
    };
    persist({ ...forms, [schema.id]: schema });
    setEditingFormId(schema.id);
  }, [forms, persist]);

  const deleteForm = useCallback(
    (id: string) => {
      const next = { ...forms };
      delete next[id];
      persist(next);
      if (editingFormId === id) setEditingFormId(null);
    },
    [forms, persist, editingFormId],
  );

  const saveForm = useCallback(
    (schema: DynamicFormSchema) => {
      persist({
        ...forms,
        [schema.id]: { ...schema, updatedAt: new Date().toISOString() },
      });
    },
    [forms, persist],
  );

  const getFormById = useCallback((id: string) => forms[id] ?? null, [forms]);

  const formList = useMemo(
    () => Object.values(forms).sort((a, b) => a.label.localeCompare(b.label)),
    [forms],
  );

  return {
    forms,
    isOpen,
    editingFormId,
    openManager,
    closeManager,
    createForm,
    deleteForm,
    saveForm,
    setEditingId: setEditingFormId,
    getFormById,
    formList,
    flushToModeler,
  };
}

// ─── useNodeFormInjector ──────────────────────────────────────────────────────

export function useNodeFormInjector(
  modelerRef: React.MutableRefObject<any>,
  manager: FormManager,
) {
  const managerRef = useRef(manager);
  useEffect(() => {
    managerRef.current = manager;
  }, [manager]);

  const activeElRef = useRef<any>(null);

  useEffect(() => {
    const m = modelerRef.current;
    if (!m) return;
    const eb = m.get?.("eventBus");
    if (!eb) return;
    const fn = ({ newSelection }: any) => {
      activeElRef.current = newSelection?.[0] ?? null;
    };
    eb.on("selection.changed", fn);
    return () => eb.off("selection.changed", fn);
  }, [modelerRef]);

  useEffect(() => {
    let observer: MutationObserver | null = null;
    let busy = false;

    const inject = () => {
      if (busy) return;
      busy = true;
      const panel = document.querySelector(PANEL_SELECTOR);
      if (!panel) {
        busy = false;
        return;
      }

      let formEntry: Element | null =
        panel.querySelector('[data-entry-id="form"]') ??
        panel.querySelector('[data-entry-id="caseForm"]') ??
        null;

      if (!formEntry) {
        const labels = panel.querySelectorAll(
          "label, .bio-properties-panel-label",
        );
        labels.forEach((lbl) => {
          if (
            !formEntry &&
            ["form", "forms"].includes(
              lbl.textContent?.toLowerCase().trim() ?? "",
            )
          ) {
            formEntry =
              lbl.closest(
                ".bio-properties-panel-entry, .bpp-properties-panel__entry",
              ) ?? lbl.parentElement;
          }
        });
      }
      if (!formEntry) {
        const inp = panel.querySelector(
          'textarea[name="form"], input[name="form"]',
        );
        if (inp)
          formEntry =
            inp.closest(".bio-properties-panel-entry") ?? inp.parentElement;
      }
      if (!formEntry || formEntry.querySelector("#" + NODE_SELECTOR_ID)) {
        busy = false;
        return;
      }

      const modeler = modelerRef.current;
      if (!modeler) {
        busy = false;
        return;
      }
      let sel: any[] = [];
      try {
        sel = modeler.get("selection").get();
      } catch {}
      const el = sel?.[0] ?? null;
      activeElRef.current = el;
      if (!el) {
        busy = false;
        return;
      }

      const bo = el.businessObject;
      const nodeCode = bo?.code || bo?.id || el.id || "";
      const currentFormId: string = typeof bo?.form === "string" ? bo.form : "";

      const root = document.createElement("div");
      root.id = NODE_SELECTOR_ID;
      root.style.cssText = "margin-top:8px;";
      renderNodeSelector(root, currentFormId, nodeCode, modeler, managerRef);
      formEntry.appendChild(root);
      busy = false;
    };

    const start = () => {
      const panel = document.querySelector(PANEL_SELECTOR);
      if (!panel) return;
      inject();
      observer = new MutationObserver(() => {
        document.getElementById(NODE_SELECTOR_ID)?.remove();
        inject();
      });
      observer.observe(panel, { childList: true, subtree: true });
    };

    const t = setTimeout(start, 700);
    return () => {
      clearTimeout(t);
      observer?.disconnect();
      document.getElementById(NODE_SELECTOR_ID)?.remove();
    };
  }, [modelerRef]);

  // Re-render sélecteur quand la liste de formulaires change
  useEffect(() => {
    const root = document.getElementById(NODE_SELECTOR_ID);
    if (!root) return;
    const modeler = modelerRef.current;
    if (!modeler) return;
    let sel: any[] = [];
    try {
      sel = modeler.get("selection").get();
    } catch {}
    const el = sel?.[0] ?? null;
    if (!el) return;
    const bo = el.businessObject;
    const nodeCode = bo?.code || bo?.id || el.id || "";
    const currentFormId: string = typeof bo?.form === "string" ? bo.form : "";
    root.innerHTML = "";
    renderNodeSelector(root, currentFormId, nodeCode, modeler, managerRef);
  }, [manager.forms, modelerRef]);
}

function renderNodeSelector(
  container: HTMLElement,
  currentFormId: string,
  nodeCode: string,
  modeler: any,
  managerRef: React.MutableRefObject<FormManager>,
) {
  const mgr = managerRef.current;
  const forms = mgr.formList;

  const lbl = document.createElement("div");
  lbl.style.cssText =
    "font-size:11px;color:var(--color-text-secondary,#666);margin-bottom:4px;font-weight:500;";
  lbl.textContent = "Formulaire lié";
  container.appendChild(lbl);

  if (forms.length === 0) {
    const btn = document.createElement("button");
    btn.style.cssText =
      "width:100%;padding:7px 10px;font-size:12px;font-weight:500;cursor:pointer;border:0.5px dashed #378ADD;border-radius:6px;background:transparent;color:#185FA5;display:flex;align-items:center;justify-content:center;gap:5px;";
    btn.innerHTML = "<span>+</span> Créer un formulaire";
    btn.addEventListener("click", () => mgr.openManager());
    container.appendChild(btn);
    return;
  }

  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:5px;align-items:center;";

  const sel = document.createElement("select");
  sel.style.cssText =
    "flex:1;font-size:13px;padding:5px 8px;border:0.5px solid var(--color-border-secondary,#ccc);border-radius:6px;background:var(--color-background-secondary,#f5f5f5);color:var(--color-text-primary,#222);cursor:pointer;";

  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = "— Aucun —";
  sel.appendChild(placeholder);

  forms.forEach((f) => {
    const opt = document.createElement("option");
    opt.value = f.id;
    opt.textContent =
      f.label +
      " (" +
      (f.fields?.length ?? 0) +
      " champ" +
      ((f.fields?.length ?? 0) !== 1 ? "s" : "") +
      ")";
    if (f.id === currentFormId) opt.selected = true;
    sel.appendChild(opt);
  });

  sel.addEventListener("change", () => {
    try {
      const modeling = modeler.get("modeling") as any;
      let elRef: any = null;
      try {
        elRef = modeler.get("selection").get()?.[0];
      } catch {}
      if (elRef) modeling.updateProperties(elRef, { form: sel.value });
    } catch (e) {
      console.warn("[DFB] updateProperties form:", e);
    }
    setTimeout(() => {
      container.innerHTML = "";
      renderNodeSelector(container, sel.value, nodeCode, modeler, managerRef);
    }, 50);
  });

  row.appendChild(sel);

  const editBtn = document.createElement("button");
  editBtn.title = currentFormId
    ? "Modifier ce formulaire"
    : "Gérer les formulaires";
  editBtn.style.cssText =
    "flex-shrink:0;padding:5px 8px;font-size:12px;cursor:pointer;border:0.5px solid var(--color-border-secondary,#ccc);border-radius:6px;background:" +
    (currentFormId ? "#185FA5" : "transparent") +
    ";color:" +
    (currentFormId ? "#fff" : "var(--color-text-secondary,#666)") +
    ";";
  editBtn.textContent = currentFormId ? "✎" : "⚙";
  editBtn.addEventListener("click", () =>
    mgr.openManager(currentFormId || undefined),
  );
  row.appendChild(editBtn);
  container.appendChild(row);

  if (currentFormId) {
    const schema = mgr.getFormById(currentFormId);
    if (schema) {
      const badge = document.createElement("div");
      badge.style.cssText =
        "margin-top:5px;display:flex;align-items:center;gap:5px;font-size:11px;padding:3px 8px;background:#E6F1FB;color:#185FA5;border-radius:5px;border:0.5px solid #B5D4F4;";
      badge.textContent =
        schema.fields.length +
        " champ" +
        (schema.fields.length !== 1 ? "s" : "") +
        " · " +
        schema.label;
      container.appendChild(badge);
    }
  }
}

// ─── FormFloatingButton ───────────────────────────────────────────────────────

export const FormFloatingButton: React.FC<{ manager: FormManager }> = ({
  manager,
}) => (
  <button
    onClick={() => manager.openManager()}
    title="Gérer les formulaires"
    style={{
      position: "fixed",
      bottom: "24px",
      right: "24px",
      width: "52px",
      height: "52px",
      borderRadius: "50%",
      background: "#185FA5",
      color: "#fff",
      border: "none",
      fontSize: "22px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 8000,
      boxShadow: "0 4px 16px rgba(24,95,165,0.35)",
    }}
  >
    <i className="ti ti-forms" aria-hidden="true" />
  </button>
);

// ─── FormManagerModal ─────────────────────────────────────────────────────────

export const FormManagerModal: React.FC<{ manager: FormManager }> = ({
  manager,
}) => {
  const { isOpen, formList, editingFormId, setEditingId } = manager;
  const [localSchema, setLocalSchema] = useState<DynamicFormSchema | null>(
    null,
  );
  const [tab, setTab] = useState<"list" | "builder" | "preview">("list");
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const dragSrcIdx = useRef<number | null>(null);

  useEffect(() => {
    if (editingFormId) {
      const s = manager.getFormById(editingFormId);
      if (s) {
        setLocalSchema(JSON.parse(JSON.stringify(s)));
        setTab("builder");
        setSelectedFieldId(null);
      }
    } else {
      setLocalSchema(null);
      setTab("list");
    }
  }, [editingFormId]);

  const selectedField = useMemo(
    () => localSchema?.fields.find((f) => f.id === selectedFieldId) ?? null,
    [localSchema, selectedFieldId],
  );

  const addField = (type: FieldType) => {
    const def = FIELD_TYPES.find((t) => t.type === type)!;
    const f: FormField = {
      id: uid(),
      type,
      code: slugify(def.label) + "_" + uid().slice(0, 4),
      label: def.label,
      required: false,
      readOnly: false,
      hidden: false,
      options: ["select", "multiselect", "radio"].includes(type)
        ? [
            { label: "Option 1", value: "option_1" },
            { label: "Option 2", value: "option_2" },
          ]
        : undefined,
    };
    setLocalSchema((p) => (p ? { ...p, fields: [...p.fields, f] } : p));
    setSelectedFieldId(f.id);
  };

  const removeField = (id: string) => {
    setLocalSchema((p) =>
      p ? { ...p, fields: p.fields.filter((f) => f.id !== id) } : p,
    );
    setSelectedFieldId((s) => (s === id ? null : s));
  };

  const duplicateField = (id: string) => {
    setLocalSchema((p) => {
      if (!p) return p;
      const i = p.fields.findIndex((f) => f.id === id);
      if (i === -1) return p;
      const copy: FormField = {
        ...p.fields[i],
        id: uid(),
        label: p.fields[i].label + " (copie)",
        code: p.fields[i].code + "_copy",
      };
      const fields = [...p.fields];
      fields.splice(i + 1, 0, copy);
      return { ...p, fields };
    });
  };

  const updateField = (id: string, patch: Partial<FormField>) =>
    setLocalSchema((p) =>
      p
        ? {
            ...p,
            fields: p.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
          }
        : p,
    );

  const moveField = (from: number, to: number) =>
    setLocalSchema((p) => {
      if (!p) return p;
      const fields = [...p.fields];
      const [m] = fields.splice(from, 1);
      fields.splice(to, 0, m);
      return { ...p, fields };
    });

  const handleSave = () => {
    if (!localSchema) return;
    manager.saveForm(localSchema);
    manager.closeManager();
  };

  if (!isOpen) return null;

  const overlay: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
  };
  const box: React.CSSProperties = {
    width: "min(960px, 96vw)",
    height: "min(700px, 94vh)",
    background: "var(--color-background-primary, #fff)",
    borderRadius: "12px",
    border: "0.5px solid var(--color-border-secondary, #ccc)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "system-ui, sans-serif",
    fontSize: "14px",
    color: "var(--color-text-primary, #222)",
    boxShadow: "0 24px 56px rgba(0,0,0,0.2)",
  };

  return (
    <div
      style={overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) manager.closeManager();
      }}
    >
      <div style={box}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 16px",
            borderBottom: "0.5px solid var(--color-border-secondary,#ccc)",
            flexShrink: 0,
          }}
        >
          {editingFormId && (
            <button
              onClick={() => {
                setEditingId(null);
                setTab("list");
              }}
              style={ghostBtn}
              title="Retour"
            >
              ←
            </button>
          )}
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontWeight: 500, fontSize: "15px" }}>
              {editingFormId
                ? localSchema?.label || "Formulaire"
                : "Gestionnaire de formulaires"}
            </p>
            {editingFormId && (
              <p
                style={{
                  margin: 0,
                  fontSize: "11px",
                  color: "var(--color-text-tertiary,#999)",
                }}
              >
                {localSchema?.fields.length ?? 0} champ
                {(localSchema?.fields.length ?? 0) !== 1 ? "s" : ""}
              </p>
            )}
          </div>

          {editingFormId && (
            <div
              style={{
                display: "flex",
                border: "0.5px solid var(--color-border-secondary,#ccc)",
                borderRadius: "7px",
                overflow: "hidden",
              }}
            >
              {(["builder", "preview"] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  disabled={
                    t === "preview" && (localSchema?.fields.length ?? 0) === 0
                  }
                  style={{
                    padding: "5px 14px",
                    fontSize: "12px",
                    border: "none",
                    cursor: "pointer",
                    background: tab === t ? "#185FA5" : "transparent",
                    color:
                      tab === t ? "#fff" : "var(--color-text-secondary,#666)",
                    opacity:
                      t === "preview" && (localSchema?.fields.length ?? 0) === 0
                        ? 0.4
                        : 1,
                    fontWeight: tab === t ? 500 : 400,
                  }}
                >
                  {t === "builder" ? "Builder" : "Aperçu"}
                </button>
              ))}
            </div>
          )}

          {editingFormId && (
            <button onClick={handleSave} style={primaryBtn}>
              Sauvegarder
            </button>
          )}
          <button
            onClick={manager.closeManager}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "22px",
              color: "var(--color-text-secondary,#888)",
              padding: "2px 6px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Corps */}
        {!editingFormId ? (
          <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "14px",
              }}
            >
              <button onClick={manager.createForm} style={primaryBtn}>
                + Nouveau formulaire
              </button>
            </div>
            {formList.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  color: "var(--color-text-tertiary,#aaa)",
                  fontSize: "14px",
                  padding: "60px 20px",
                }}
              >
                <i
                  className="ti ti-forms"
                  style={{ fontSize: "48px" }}
                  aria-hidden="true"
                />
                Aucun formulaire. Créez-en un pour commencer.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: "10px",
                }}
              >
                {formList.map((f) => {
                  const types = uniqueTypes(f.fields);
                  return (
                    <div
                      key={f.id}
                      style={{
                        background: "var(--color-background-primary,#fff)",
                        border: "0.5px solid var(--color-border-tertiary,#eee)",
                        borderRadius: "10px",
                        padding: "14px 16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: 500,
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {f.label}
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "11px",
                            color: "var(--color-text-tertiary,#999)",
                          }}
                        >
                          {f.fields?.length ?? 0} champ
                          {(f.fields?.length ?? 0) !== 1 ? "s" : ""} ·{" "}
                          {new Date(f.updatedAt).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "3px",
                        }}
                      >
                        {types.slice(0, 5).map((type) => {
                          const def = FIELD_TYPES.find((t) => t.type === type);
                          return (
                            <span
                              key={type}
                              style={{
                                fontSize: "10px",
                                background:
                                  "var(--color-background-secondary,#f5f5f5)",
                                borderRadius: "4px",
                                padding: "1px 5px",
                                color: "var(--color-text-secondary,#666)",
                              }}
                            >
                              {def?.label}
                            </span>
                          );
                        })}
                        {types.length > 5 && (
                          <span
                            style={{
                              fontSize: "10px",
                              color: "var(--color-text-tertiary,#aaa)",
                            }}
                          >
                            +{types.length - 5}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          marginTop: "2px",
                        }}
                      >
                        <button
                          onClick={() => setEditingId(f.id)}
                          style={{
                            ...secondaryBtn,
                            flex: 1,
                            justifyContent: "center",
                            fontSize: "12px",
                            padding: "5px 8px",
                          }}
                        >
                          ✎ Modifier
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Supprimer "' + f.label + '" ?'))
                              manager.deleteForm(f.id);
                          }}
                          style={{
                            ...secondaryBtn,
                            fontSize: "12px",
                            padding: "5px 8px",
                            color: "#E24B4A",
                            borderColor: "#F09595",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : tab === "preview" ? (
          <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px" }}>
            <FormPreview schema={localSchema!} />
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
            {/* Sidebar types */}
            <aside
              style={{
                width: "155px",
                flexShrink: 0,
                borderRight: "0.5px solid var(--color-border-tertiary,#eee)",
                padding: "10px 8px",
                overflowY: "auto",
              }}
            >
              <p style={secLbl}>Types de champs</p>
              {FIELD_TYPES.map(({ type, label, icon }) => (
                <div
                  key={type}
                  onClick={() => addField(type)}
                  style={typeChip}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.background =
                      "var(--color-background-secondary,#f5f5f5)")
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.background =
                      "var(--color-background-primary,#fff)")
                  }
                >
                  <i
                    className={"ti " + icon}
                    style={{
                      fontSize: "13px",
                      color: "var(--color-text-secondary,#666)",
                    }}
                    aria-hidden="true"
                  />
                  <span style={{ fontSize: "12px" }}>{label}</span>
                </div>
              ))}
            </aside>

            {/* Canvas */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  marginBottom: "6px",
                  paddingBottom: "8px",
                  borderBottom: "0.5px solid var(--color-border-tertiary,#eee)",
                }}
              >
                <input
                  value={localSchema?.label ?? ""}
                  placeholder="Nom du formulaire"
                  onChange={(e) =>
                    setLocalSchema((p) =>
                      p ? { ...p, label: e.target.value } : p,
                    )
                  }
                  style={{ flex: 1, ...inpStyle, fontWeight: 500 }}
                />
              </div>

              {(localSchema?.fields.length ?? 0) === 0 && (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    color: "var(--color-text-tertiary,#aaa)",
                    fontSize: "13px",
                    padding: "40px",
                  }}
                >
                  <i
                    className="ti ti-drag-drop"
                    style={{ fontSize: "36px" }}
                    aria-hidden="true"
                  />
                  Cliquez sur un type de champ dans la liste à gauche
                </div>
              )}

              {localSchema?.fields.map((field, idx) => (
                <div
                  key={field.id}
                  draggable
                  onDragStart={() => {
                    dragSrcIdx.current = idx;
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (
                      dragSrcIdx.current !== null &&
                      dragSrcIdx.current !== idx
                    )
                      moveField(dragSrcIdx.current, idx);
                    dragSrcIdx.current = null;
                  }}
                  onClick={() => setSelectedFieldId(field.id)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    padding: "9px 10px",
                    border:
                      "0.5px solid " +
                      (selectedFieldId === field.id
                        ? "#378ADD"
                        : "var(--color-border-tertiary,#eee)"),
                    borderRadius: "7px",
                    background: "var(--color-background-primary,#fff)",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <span
                    style={{
                      color: "var(--color-text-tertiary,#ccc)",
                      cursor: "grab",
                      paddingTop: "2px",
                      fontSize: "16px",
                    }}
                  >
                    ⠿
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 500,
                        marginBottom: "3px",
                        display: "flex",
                        gap: "4px",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {field.label}
                      {field.required && <Pill c="blue">Requis</Pill>}
                      {field.hidden && <Pill c="gray">Caché</Pill>}
                      {field.conditionField && (
                        <Pill c="green">Conditionnel</Pill>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--color-text-tertiary,#999)",
                        background: "var(--color-background-secondary,#f5f5f5)",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        display: "inline-block",
                      }}
                    >
                      {FIELD_TYPES.find((t) => t.type === field.type)?.label}
                      {" · "}
                      <code style={{ fontFamily: "monospace" }}>
                        {field.code}
                      </code>
                      {field.options &&
                        " · " + field.options.length + " options"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "2px" }}>
                    <IBtn
                      title="Dupliquer"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateField(field.id);
                      }}
                    >
                      ⧉
                    </IBtn>
                    <IBtn
                      title="Supprimer"
                      danger
                      onClick={(e) => {
                        e.stopPropagation();
                        removeField(field.id);
                      }}
                    >
                      ✕
                    </IBtn>
                  </div>
                </div>
              ))}
            </div>

            {/* Propriétés */}
            <aside
              style={{
                width: "225px",
                flexShrink: 0,
                borderLeft: "0.5px solid var(--color-border-tertiary,#eee)",
                overflowY: "auto",
                padding: "10px 12px",
              }}
            >
              {!selectedField ? (
                <p
                  style={{
                    fontSize: "12px",
                    color: "var(--color-text-tertiary,#aaa)",
                    textAlign: "center",
                    marginTop: "24px",
                  }}
                >
                  Sélectionnez un champ pour éditer ses propriétés
                </p>
              ) : (
                <FieldProps
                  field={selectedField}
                  allFields={localSchema?.fields ?? []}
                  onChange={(patch) => updateField(selectedField.id, patch)}
                />
              )}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── FieldProps ───────────────────────────────────────────────────────────────

const FieldProps: React.FC<{
  field: FormField;
  allFields: FormField[];
  onChange: (patch: Partial<FormField>) => void;
}> = ({ field, allFields, onChange }) => {
  const hasOptions = ["select", "multiselect", "radio"].includes(field.type);
  const isSep = field.type === "separator";
  const others = allFields.filter(
    (f) => f.id !== field.id && f.type !== "separator",
  );

  const addOption = () => {
    const opts = [...(field.options ?? [])];
    opts.push({
      label: "Option " + (opts.length + 1),
      value: "option_" + (opts.length + 1),
    });
    onChange({ options: opts });
  };
  const updOpt = (i: number, patch: Partial<SelectOption>) => {
    const opts = [...(field.options ?? [])];
    opts[i] = { ...opts[i], ...patch };
    onChange({ options: opts });
  };
  const delOpt = (i: number) => {
    const opts = [...(field.options ?? [])];
    opts.splice(i, 1);
    onChange({ options: opts });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
      <p style={secLbl}>Propriétés</p>
      {!isSep && (
        <>
          <PF label="Label">
            {" "}
            <input
              style={inpStyle}
              value={field.label}
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </PF>
          <PF label="Clé (code)">
            {" "}
            <input
              style={{ ...inpStyle, fontFamily: "monospace", fontSize: "12px" }}
              value={field.code}
              onChange={(e) => onChange({ code: slugify(e.target.value) })}
            />
          </PF>
          <PF label="Type">
            <select
              style={inpStyle}
              value={field.type}
              onChange={(e) => onChange({ type: e.target.value as FieldType })}
            >
              {FIELD_TYPES.filter((t) => t.type !== "separator").map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                </option>
              ))}
            </select>
          </PF>
          <PF label="Placeholder">
            {" "}
            <input
              style={inpStyle}
              value={field.placeholder ?? ""}
              onChange={(e) => onChange({ placeholder: e.target.value })}
            />
          </PF>
          <PF label="Valeur défaut">
            <input
              style={inpStyle}
              value={field.defaultValue ?? ""}
              onChange={(e) => onChange({ defaultValue: e.target.value })}
            />
          </PF>
          <PF label="Texte d'aide">
            {" "}
            <input
              style={inpStyle}
              value={field.helpText ?? ""}
              onChange={(e) => onChange({ helpText: e.target.value })}
            />
          </PF>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <CBox
              label="Requis"
              checked={!!field.required}
              onChange={(v) => onChange({ required: v })}
            />
            <CBox
              label="Lecture seule"
              checked={!!field.readOnly}
              onChange={(v) => onChange({ readOnly: v })}
            />
            <CBox
              label="Caché"
              checked={!!field.hidden}
              onChange={(v) => onChange({ hidden: v })}
            />
          </div>
        </>
      )}
      {(field.type === "text" || field.type === "textarea") && (
        <>
          <p style={{ ...secLbl, marginTop: "4px" }}>Validation</p>
          <div style={{ display: "flex", gap: "6px" }}>
            <PF label="Min">
              <input
                type="number"
                style={{ ...inpStyle, width: "68px" }}
                value={field.min ?? ""}
                onChange={(e) =>
                  onChange({
                    min: e.target.value ? +e.target.value : undefined,
                  })
                }
              />
            </PF>
            <PF label="Max">
              <input
                type="number"
                style={{ ...inpStyle, width: "68px" }}
                value={field.max ?? ""}
                onChange={(e) =>
                  onChange({
                    max: e.target.value ? +e.target.value : undefined,
                  })
                }
              />
            </PF>
          </div>
          <PF label="Pattern (regex)">
            <input
              style={{ ...inpStyle, fontFamily: "monospace", fontSize: "11px" }}
              value={field.pattern ?? ""}
              onChange={(e) => onChange({ pattern: e.target.value })}
            />
          </PF>
        </>
      )}
      {field.type === "number" && (
        <div style={{ display: "flex", gap: "6px" }}>
          <PF label="Min">
            <input
              type="number"
              style={{ ...inpStyle, width: "80px" }}
              value={field.min ?? ""}
              onChange={(e) =>
                onChange({ min: e.target.value ? +e.target.value : undefined })
              }
            />
          </PF>
          <PF label="Max">
            <input
              type="number"
              style={{ ...inpStyle, width: "80px" }}
              value={field.max ?? ""}
              onChange={(e) =>
                onChange({ max: e.target.value ? +e.target.value : undefined })
              }
            />
          </PF>
        </div>
      )}
      {hasOptions && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <p style={secLbl}>Options</p>
            <button
              onClick={addOption}
              style={{ ...secondaryBtn, fontSize: "11px", padding: "2px 7px" }}
            >
              + Ajouter
            </button>
          </div>
          {(field.options ?? []).map((opt, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: "4px",
                marginBottom: "4px",
                alignItems: "center",
              }}
            >
              <input
                style={{ ...inpStyle, flex: 1, fontSize: "12px" }}
                value={opt.label}
                placeholder="Label"
                onChange={(e) =>
                  updOpt(i, {
                    label: e.target.value,
                    value: slugify(e.target.value) || opt.value,
                  })
                }
              />
              <IBtn title="Supprimer" danger onClick={() => delOpt(i)}>
                ✕
              </IBtn>
            </div>
          ))}
        </div>
      )}
      {!isSep && others.length > 0 && (
        <div>
          <p style={{ ...secLbl, marginTop: "4px", marginBottom: "5px" }}>
            Affichage conditionnel
          </p>
          <PF label="Visible si champ">
            <select
              style={inpStyle}
              value={field.conditionField ?? ""}
              onChange={(e) =>
                onChange({ conditionField: e.target.value || undefined })
              }
            >
              <option value="">— aucun —</option>
              {others.map((f) => (
                <option key={f.id} value={f.code}>
                  {f.label}
                </option>
              ))}
            </select>
          </PF>
          {field.conditionField && (
            <PF label="Vaut">
              <input
                style={inpStyle}
                value={field.conditionValue ?? ""}
                placeholder="valeur"
                onChange={(e) => onChange({ conditionValue: e.target.value })}
              />
            </PF>
          )}
        </div>
      )}
    </div>
  );
};

// ─── FormPreview ──────────────────────────────────────────────────────────────

const FormPreview: React.FC<{ schema: DynamicFormSchema }> = ({ schema }) => {
  const [vals, setVals] = useState<Record<string, any>>({});
  const set = (code: string, v: any) => setVals((p) => ({ ...p, [code]: v }));
  const visible = (f: FormField) =>
    !f.conditionField ||
    String(vals[f.conditionField] ?? "") === (f.conditionValue ?? "");

  return (
    <div style={{ maxWidth: "480px", margin: "0 auto" }}>
      <p style={{ fontSize: "17px", fontWeight: 500, marginBottom: "20px" }}>
        {schema.label}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {schema.fields.filter(visible).map((f) => {
          if (f.hidden) return null;
          if (f.type === "separator")
            return (
              <hr
                key={f.id}
                style={{
                  border: "none",
                  borderTop: "0.5px solid var(--color-border-tertiary,#eee)",
                }}
              />
            );
          return (
            <div key={f.id}>
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                {f.label}
                {f.required && (
                  <span style={{ color: "#E24B4A", marginLeft: "3px" }}>*</span>
                )}
              </label>
              {f.helpText && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "var(--color-text-tertiary,#999)",
                    marginBottom: "4px",
                  }}
                >
                  {f.helpText}
                </p>
              )}
              <PInput
                field={f}
                value={vals[f.code]}
                onChange={(v) => set(f.code, v)}
              />
            </div>
          );
        })}
      </div>
      <button
        style={{
          ...primaryBtn,
          marginTop: "20px",
          width: "100%",
          justifyContent: "center",
        }}
      >
        Soumettre
      </button>
    </div>
  );
};

const PInput: React.FC<{
  field: FormField;
  value: any;
  onChange: (v: any) => void;
}> = ({ field, value, onChange }) => {
  const b: React.CSSProperties = {
    width: "100%",
    padding: "7px 10px",
    border: "0.5px solid var(--color-border-secondary,#ccc)",
    borderRadius: "6px",
    fontSize: "13px",
    background: "var(--color-background-secondary,#f5f5f5)",
    color: "var(--color-text-primary,#222)",
    boxSizing: "border-box",
  };
  if (field.type === "textarea")
    return (
      <textarea
        style={{
          ...b,
          minHeight: "70px",
          resize: "vertical",
          fontFamily: "inherit",
        }}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        readOnly={field.readOnly}
      />
    );
  if (field.type === "boolean")
    return (
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "13px",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={field.readOnly}
        />{" "}
        {field.label}
      </label>
    );
  if (field.type === "select")
    return (
      <select
        style={b}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={field.readOnly}
      >
        <option value="">— Sélectionner —</option>
        {(field.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  if (field.type === "multiselect")
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {(field.options ?? []).map((o) => (
          <label
            key={o.value}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={Array.isArray(value) && value.includes(o.value)}
              onChange={(e) => {
                const a: string[] = Array.isArray(value) ? [...value] : [];
                e.target.checked
                  ? a.push(o.value)
                  : a.splice(a.indexOf(o.value), 1);
                onChange(a);
              }}
            />{" "}
            {o.label}
          </label>
        ))}
      </div>
    );
  if (field.type === "radio")
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {(field.options ?? []).map((o) => (
          <label
            key={o.value}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name={field.code}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />{" "}
            {o.label}
          </label>
        ))}
      </div>
    );
  if (field.type === "date")
    return (
      <input
        type="date"
        style={b}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={field.readOnly}
      />
    );
  if (field.type === "number")
    return (
      <input
        type="number"
        style={b}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        min={field.min}
        max={field.max}
        readOnly={field.readOnly}
      />
    );
  if (field.type === "file")
    return (
      <input
        type="file"
        style={{ ...b, padding: "5px" }}
        disabled={field.readOnly}
      />
    );
  return (
    <input
      type="text"
      style={b}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      readOnly={field.readOnly}
    />
  );
};

// ─── Micro-composants ─────────────────────────────────────────────────────────

const Pill: React.FC<{
  c: "blue" | "gray" | "green";
  children: React.ReactNode;
}> = ({ c, children }) => {
  const m = {
    blue: { bg: "#E6F1FB", col: "#185FA5", bd: "#B5D4F4" },
    gray: {
      bg: "var(--color-background-secondary,#f5f5f5)",
      col: "var(--color-text-tertiary,#999)",
      bd: "var(--color-border-tertiary,#eee)",
    },
    green: { bg: "#EAF3DE", col: "#3B6D11", bd: "#C0DD97" },
  }[c];
  return (
    <span
      style={{
        fontSize: "10px",
        background: m.bg,
        color: m.col,
        border: "0.5px solid " + m.bd,
        borderRadius: "4px",
        padding: "1px 5px",
      }}
    >
      {children}
    </span>
  );
};

const IBtn: React.FC<{
  title: string;
  danger?: boolean;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}> = ({ title, danger, onClick, children }) => (
  <button
    title={title}
    onClick={onClick}
    style={{
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "3px 5px",
      borderRadius: "4px",
      color: danger ? "#E24B4A" : "var(--color-text-secondary,#666)",
      fontSize: "13px",
      lineHeight: 1,
    }}
    onMouseEnter={(e) =>
      ((e.currentTarget as HTMLButtonElement).style.background =
        "var(--color-background-secondary,#f5f5f5)")
    }
    onMouseLeave={(e) =>
      ((e.currentTarget as HTMLButtonElement).style.background = "none")
    }
  >
    {children}
  </button>
);

const PF: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <label
      style={{
        fontSize: "11px",
        color: "var(--color-text-secondary,#666)",
        display: "block",
        marginBottom: "3px",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const CBox: React.FC<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "12px",
      cursor: "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ accentColor: "#185FA5" }}
    />
    {label}
  </label>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const secLbl: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 500,
  color: "var(--color-text-secondary,#666)",
  textTransform: "uppercase",
  letterSpacing: ".05em",
  margin: 0,
};
const inpStyle: React.CSSProperties = {
  width: "100%",
  fontSize: "13px",
  padding: "5px 8px",
  border: "0.5px solid var(--color-border-secondary,#ccc)",
  borderRadius: "6px",
  background: "var(--color-background-secondary,#f5f5f5)",
  color: "var(--color-text-primary,#222)",
  boxSizing: "border-box",
};
const primaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  padding: "7px 14px",
  fontSize: "13px",
  fontWeight: 500,
  border: "none",
  borderRadius: "6px",
  background: "#185FA5",
  color: "#fff",
  cursor: "pointer",
};
const secondaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  padding: "6px 12px",
  fontSize: "13px",
  border: "0.5px solid var(--color-border-secondary,#ccc)",
  borderRadius: "6px",
  background: "var(--color-background-primary,#fff)",
  color: "var(--color-text-primary,#222)",
  cursor: "pointer",
};
const ghostBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "18px",
  color: "var(--color-text-secondary,#666)",
  padding: "2px 6px",
  borderRadius: "4px",
};
const typeChip: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "6px 8px",
  marginBottom: "3px",
  border: "0.5px solid var(--color-border-tertiary,#eee)",
  borderRadius: "6px",
  background: "var(--color-background-primary,#fff)",
  cursor: "pointer",
  userSelect: "none",
  transition: "background .12s",
};

// ─── createFormPaletteModule ──────────────────────────────────────────────────

const PALETTE_BTN_ID = "dfb-palette-btn";
const PALETTE_STYLE_ID = "dfb-palette-style";
const ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><line x1="7" y1="9" x2="17" y2="9"/><line x1="7" y1="12" x2="13" y2="12"/><line x1="7" y1="15" x2="11" y2="15"/></svg>';
function injectPaletteStyles() {
  if (document.getElementById(PALETTE_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = PALETTE_STYLE_ID;
  style.textContent = [
    "#" + PALETTE_BTN_ID + " {",
    "  display:flex!important;align-items:center;justify-content:center;",
    "  width:100%;padding:10px 4px;margin:0;",
    "  background:#fff;color:#333;", // ← même couleur que les autres icônes
    "  border:none;border-top:0.5px solid var(--color-border-tertiary,#e0e0e0);",
    "  border-radius:0;cursor:pointer;box-sizing:border-box;",
    "  transition:background .15s;position:relative;",
    "}",
    "#" + PALETTE_BTN_ID + ":hover { background:#f0f0f0!important; }",
    ".dfb-palette-sep { height:0;margin:0; }", // ← supprime le séparateur visible
    "#" + PALETTE_BTN_ID + " .dfb-count {",
    "  position:absolute;top:3px;right:3px;background:#185FA5;color:#fff;",
    "  border-radius:10px;font-size:9px;font-weight:700;padding:0 4px;min-width:14px;",
    "  text-align:center;line-height:14px;",
    "}",
  ].join("\n");
  document.head.appendChild(style);
}

function removePaletteBtn() {
  document.getElementById(PALETTE_BTN_ID)?.remove();
  document.querySelector(".dfb-palette-sep")?.remove();
}

export function createFormPaletteModule(
  onOpen: () => void,
  getCount?: () => number,
) {
  function FormPaletteExtension(this: any, eventBus: any) {
    injectPaletteStyles();

    const updateBadge = () => {
      const badge = document.querySelector(
        "#" + PALETTE_BTN_ID + " .dfb-count",
      ) as HTMLElement | null;
      if (badge && getCount) {
        const n = getCount();
        badge.textContent = String(n);
        badge.style.display = n > 0 ? "block" : "none";
      }
    };

    const tryInject = (): boolean => {
      const palette = document.querySelector(".djs-palette");
      if (!palette) return false;
      if (document.getElementById(PALETTE_BTN_ID)) {
        updateBadge();
        return true;
      }

      const sep = document.createElement("div");
      sep.className = "dfb-palette-sep";

      const btn = document.createElement("button");
      btn.id = PALETTE_BTN_ID;
      btn.title = "Gérer les formulaires dynamiques";
      btn.innerHTML =
        ICON_SVG + '<span class="dfb-count" style="display:none">0</span>';
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        onOpen();
      });

      palette.appendChild(sep);
      palette.appendChild(btn);
      updateBadge();
      return true;
    };

    let injected = false;
    const attempt = () => {
      if (!injected) injected = tryInject();
    };

    eventBus.on("canvas.init", attempt);
    eventBus.on("palette.changed", attempt);
    eventBus.on("diagram.init", attempt);
    eventBus.on("elements.changed", updateBadge);

    const observer = new MutationObserver(() => {
      if (!injected) injected = tryInject();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    eventBus.on("diagram.destroy", () => {
      observer.disconnect();
      removePaletteBtn();
    });
  }

  (FormPaletteExtension as any).$inject = ["eventBus"];

  return {
    __init__: ["formPaletteExtension"],
    formPaletteExtension: ["type", FormPaletteExtension],
  };
}
